import React, { useEffect } from 'react';

import { Box, Button, Grid } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { ShowErrors } from 'common/components';
import {
    ReactHookFormArrayEmails,
    ReactHookFormArrayPhones,
    ReactHookFormInput,
    ReactHookFormSelect,
} from 'common/components/form/bootstrap';
import yupResolver from 'common/utils/yupResolver';
//import { getPhoneCodeOptions } from 'common/utils/helpers';

import * as actions from '../actions';
import { getUserOptions } from '../helpers';
import * as selectors from '../selectors';
import validate from '../validate';

//import ReactHookFormArrayPhones from './ReactHookFormArrayPhones';

const ContactForm = ({
    closeModal,
    fetchContact,
    fetchPhoneCodes,
    handleClickBulkItems,
    initialValues,
    isFetchingPhoneCodes,
    isNewRecord,
    onSuccessSave,
    phoneCodes,
    redirect,
    save,
    saveErrors,
    users,
}) => {
    const { t } = useTranslation();

    const { control, getValues, handleSubmit, reset } = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validate),
    });

    useEffect(() => reset(initialValues), [initialValues]);

    useEffect(() => {
        fetchPhoneCodes();
    }, []);

    const isArchived = getValues('status_flag') === 'archived';
    const isDisabled = false;

    return (
        <form>
            <ReactHookFormInput
                control={control}
                fullWidth
                disabled={isDisabled}
                label={`${t('Name', { count: 2 })} *`}
                name="name"
            />

            <ReactHookFormInput
                control={control}
                fullWidth
                disabled={isDisabled}
                label={t('Business')}
                name="company_name"
            />

            <ReactHookFormInput
                control={control}
                fullWidth
                disabled={isDisabled}
                label={t('Position')}
                name="company_position"
            />
            <ReactHookFormInput
                control={control}
                fullWidth
                disabled={isDisabled}
                label={t('Tax ID')}
                name="rfc"
            />
            <Box mt={2} mb={2}>
                <ReactHookFormArrayPhones
                    codes={phoneCodes}
                    control={control}
                    disabled={isDisabled || isFetchingPhoneCodes}
                    name="telephones"
                />
            </Box>

            <Box mt={2} mb={2}>
                <ReactHookFormArrayEmails
                    control={control}
                    disabled={isDisabled}
                    name="emails"
                />
            </Box>

            <ReactHookFormSelect
                control={control}
                disabled={isDisabled}
                label={t('Installer')}
                name="user"
                options={getUserOptions(users)}
            />

            <ShowErrors errors={saveErrors} />

            <Grid container mt={1}>
                <Grid
                    size="grow"
                    sx={{
                        display: 'flex',
                        flexDirection: { md: 'row', xs: 'column' },
                        justifyContent: { md: 'right', xs: 'center' },
                    }}
                >
                    {!isNewRecord && (
                        <>
                            <Button
                                onClick={() =>
                                    handleClickBulkItems(
                                        [getValues('id')],
                                        'archived',
                                        () => fetchContact(getValues('id')),
                                    )
                                }
                                sx={{
                                    mr: { md: 2, xs: 0 },
                                    order: { md: 1, xs: 2 },
                                    width: { md: 'auto', xs: '100%' },
                                }}
                                type="button"
                                variant="text"
                                visible={!isArchived}
                            >
                                {t('Archive')}
                            </Button>

                            <Button
                                onClick={() =>
                                    handleClickBulkItems(
                                        [getValues('id')],
                                        'active',
                                        () => fetchContact(getValues('id')),
                                    )
                                }
                                sx={{
                                    mr: { md: 2, xs: 0 },
                                    order: { md: 1, xs: 2 },
                                    width: { md: 'auto', xs: '100%' },
                                }}
                                type="button"
                                variant="text"
                                visible={isArchived}
                            >
                                {t('Unarchive')}
                            </Button>

                            <Button
                                color="error"
                                onClick={() =>
                                    handleClickBulkItems(
                                        [getValues('id')],
                                        'deleted',
                                        () => {
                                            closeModal();
                                            redirect('/contacts');
                                        },
                                    )
                                }
                                sx={{
                                    mr: { md: 2, xs: 0 },
                                    order: { md: 1, xs: 2 },
                                    width: { md: 'auto', xs: '100%' },
                                }}
                                type="button"
                                variant="text"
                                visible={isArchived}
                            >
                                {t('Delete')}
                            </Button>
                        </>
                    )}

                    <Button
                        onClick={handleSubmit((values) =>
                            save(values, onSuccessSave),
                        )}
                        sx={{
                            mb: { md: 0, xs: 2 },
                            order: { md: 2, xs: 1 },
                            width: { md: 'auto', xs: '100%' },
                        }}
                        variant="outlined"
                        visible={!isArchived}
                    >
                        {t('Save')}
                    </Button>
                </Grid>
            </Grid>
        </form>
    );
};

ContactForm.propTypes = {
    closeModal: PropTypes.func,
    fetchContact: PropTypes.func,
    fetchPhoneCodes: PropTypes.func,
    handleClickBulkItems: PropTypes.func,
    initialValues: PropTypes.object,
    isFetchingPhoneCodes: PropTypes.bool,
    isNewRecord: PropTypes.bool,
    onSuccessSave: PropTypes.func,
    phoneCodes: PropTypes.array,
    redirect: PropTypes.func,
    save: PropTypes.func,
    saveErrors: PropTypes.array,
    users: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
    initialValues: selectors.getInitialValues,
    isFetchingPhoneCodes: selectors.getIsFetchingPhoneCodes,
    phoneCodes: selectors.getPhoneCodesData,
    saveErrors: selectors.getContactErrors,
});

const mapDispatchToProps = (dispatch) => ({
    fetchPhoneCodes: () => dispatch(actions.fetchPhoneCodes()),
    save: (values, onSuccess) =>
        dispatch(actions.saveContact(values, onSuccess)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ContactForm);
